/**
 * A small software rasteriser for the game's art.
 *
 * Everything is drawn in "art pixels" (the same units the drawers have always
 * used) but painted into a buffer that is `scale` times denser in each
 * direction. Shapes are filled with exact edge anti-aliasing, gradients and
 * curves are available, and the buffer is only turned into a canvas at the
 * very end. Scaled down to the art's logical size it is still a sprite of the
 * same dimensions, so positions, anchors and hit tests never change; shown at
 * full size it is smooth vector-style art.
 */

export type Color = string;

export interface Point {
  x: number;
  y: number;
}

/** Density of the pixel buffer: every art pixel is `ART_SCALE` x `ART_SCALE` buffer pixels. */
export const ART_SCALE = 4;

/** Width (in art pixels) of the dark border drawn around sprites. */
export const OUTLINE_WIDTH = 0.8;

type Stops = ReadonlyArray<readonly [number, Color]>;

/**
 * A fill that is not a flat colour. Coordinates are in art pixels in the
 * drawing space (translation by `ox`/`oy` applies), except for the `box`
 * kinds, which are relative to the shape being filled.
 */
export type Gradient =
  | { type: 'linear'; x0: number; y0: number; x1: number; y1: number; stops: Stops }
  | { type: 'radial'; cx: number; cy: number; r: number; stops: Stops }
  /** Runs across the shape's bounding box at `angle` degrees (90 = top to bottom). */
  | { type: 'box'; angle: number; stops: Stops }
  /** Centred at (`fx`, `fy`) as fractions of the shape's bounding box and stretched to its aspect; `reach` 1 ends at the corners of a square shape. */
  | { type: 'boxRadial'; fx: number; fy: number; reach: number; stops: Stops };

export type Paint = Color | Gradient;

const colorCache = new Map<string, number>();

/** '#rrggbb' or '#rrggbbaa' -> packed ABGR (the byte order of ImageData on little-endian machines). */
export function packColor(color: Color): number {
  const cached = colorCache.get(color);
  if (cached !== undefined) return cached;

  const hex = color.startsWith('#') ? color.slice(1) : color;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const a = hex.length >= 8 ? parseInt(hex.slice(6, 8), 16) : 255;
  const packed = ((a << 24) | (b << 16) | (g << 8) | r) >>> 0;
  colorCache.set(color, packed);
  return packed;
}

function clampByte(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

/** Multiply a colour's RGB channels; < 1 darkens, > 1 lightens. */
export function shade(color: Color, factor: number): Color {
  const hex = color.slice(1);
  const channels = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
  const next = channels.map((c) =>
    factor >= 1 ? clampByte(c + (255 - c) * (factor - 1)) : clampByte(c * factor),
  );
  return `#${next.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

/** Linear blend of two '#rrggbb' colours; t = 0 gives a, t = 1 gives b. */
export function mix(a: Color, b: Color, t: number): Color {
  const ca = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16));
  const cb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16));
  return `#${ca.map((v, i) => clampByte(v + (cb[i]! - v) * t).toString(16).padStart(2, '0')).join('')}`;
}

/** Deterministic PRNG so procedural details (speckles, book colours) are stable. */
export function seededRandom(seed: number): () => number {
  let state = seed >>> 0 || 1;
  return () => {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    return ((state >>> 0) % 100_000) / 100_000;
  };
}

/* -------------------------------------------------------------------------- */
/* Gradient helpers                                                           */
/* -------------------------------------------------------------------------- */

/** A soft light-to-dark wash over a shape: lighter at the top, darker at the bottom. */
export function fade(color: Color, top = 1.12, bottom = 0.88, angle = 90): Gradient {
  return { type: 'box', angle, stops: [[0, shade(color, top)], [1, shade(color, bottom)]] };
}

/** A rounded, lit look: highlight up and to the left, shadow down and to the right. */
export function puff(color: Color, light = 1.22, dark = 0.82): Gradient {
  return {
    type: 'boxRadial',
    fx: 0.34,
    fy: 0.28,
    reach: 1.15,
    stops: [[0, shade(color, light)], [0.55, color], [1, shade(color, dark)]],
  };
}

/** Two colours across a shape. */
export function between(a: Color, b: Color, angle = 90): Gradient {
  return { type: 'box', angle, stops: [[0, a], [1, b]] };
}

/* -------------------------------------------------------------------------- */
/* Paint evaluation                                                           */
/* -------------------------------------------------------------------------- */

interface Bounds {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** Resolves a paint to per-pixel colour. Channel fields hold the latest sample. */
class Painter {
  r = 0;
  g = 0;
  b = 0;
  a = 255;
  readonly flat: boolean;
  private readonly stops: Array<{ t: number; r: number; g: number; b: number; a: number }> = [];
  private readonly kind: Gradient['type'] | 'solid';
  private ax = 0;
  private ay = 0;
  private bx = 0;
  private by = 0;
  private radius = 1;

  constructor(paint: Paint, bounds: Bounds, scale: number, ox: number, oy: number) {
    if (typeof paint === 'string') {
      const packed = packColor(paint);
      this.r = packed & 255;
      this.g = (packed >>> 8) & 255;
      this.b = (packed >>> 16) & 255;
      this.a = packed >>> 24;
      this.flat = true;
      this.kind = 'solid';
      return;
    }
    this.flat = false;
    this.kind = paint.type;
    for (const [t, color] of paint.stops) {
      const p = packColor(color);
      this.stops.push({ t, r: p & 255, g: (p >>> 8) & 255, b: (p >>> 16) & 255, a: p >>> 24 });
    }
    const bw = bounds.x1 - bounds.x0;
    const bh = bounds.y1 - bounds.y0;
    switch (paint.type) {
      case 'linear':
        this.ax = (paint.x0 + ox) * scale;
        this.ay = (paint.y0 + oy) * scale;
        this.bx = (paint.x1 + ox) * scale;
        this.by = (paint.y1 + oy) * scale;
        break;
      case 'radial':
        this.ax = (paint.cx + ox) * scale;
        this.ay = (paint.cy + oy) * scale;
        this.radius = Math.max(paint.r * scale, 1e-3);
        break;
      case 'box': {
        const rad = (paint.angle * Math.PI) / 180;
        const dx = Math.cos(rad);
        const dy = Math.sin(rad);
        const half = (Math.abs(dx) * bw + Math.abs(dy) * bh) / 2;
        const cx = bounds.x0 + bw / 2;
        const cy = bounds.y0 + bh / 2;
        this.ax = cx - dx * half;
        this.ay = cy - dy * half;
        this.bx = cx + dx * half;
        this.by = cy + dy * half;
        break;
      }
      case 'boxRadial':
        this.ax = bounds.x0 + bw * paint.fx;
        this.ay = bounds.y0 + bh * paint.fy;
        this.bx = Math.max(bw / 2, 1e-3);
        this.by = Math.max(bh / 2, 1e-3);
        this.radius = Math.max(paint.reach / Math.SQRT2, 1e-3);
        break;
    }
    this.sample(0, 0);
  }

  sample(x: number, y: number): void {
    if (this.kind === 'solid') return;
    let t: number;
    if (this.kind === 'linear' || this.kind === 'box') {
      const dx = this.bx - this.ax;
      const dy = this.by - this.ay;
      const len2 = dx * dx + dy * dy || 1;
      t = ((x - this.ax) * dx + (y - this.ay) * dy) / len2;
    } else if (this.kind === 'boxRadial') {
      t = Math.hypot((x - this.ax) / this.bx, (y - this.ay) / this.by) / this.radius;
    } else {
      t = Math.hypot(x - this.ax, y - this.ay) / this.radius;
    }
    const stops = this.stops;
    const last = stops.length - 1;
    if (t <= stops[0]!.t) {
      this.load(stops[0]!, stops[0]!, 0);
    } else if (t >= stops[last]!.t) {
      this.load(stops[last]!, stops[last]!, 0);
    } else {
      let i = 0;
      while (i < last - 1 && t > stops[i + 1]!.t) i += 1;
      const s0 = stops[i]!;
      const s1 = stops[i + 1]!;
      this.load(s0, s1, (t - s0.t) / (s1.t - s0.t || 1));
    }
  }

  private load(
    s0: { r: number; g: number; b: number; a: number },
    s1: { r: number; g: number; b: number; a: number },
    f: number,
  ): void {
    this.r = s0.r + (s1.r - s0.r) * f;
    this.g = s0.g + (s1.g - s0.g) * f;
    this.b = s0.b + (s1.b - s0.b) * f;
    this.a = s0.a + (s1.a - s0.a) * f;
  }
}

/* -------------------------------------------------------------------------- */
/* Paths                                                                      */
/* -------------------------------------------------------------------------- */

/** Builds curved outlines out of lines and Béziers; `points()` flattens it for `PixelCanvas.path`. */
export class PathBuilder {
  private readonly subpaths: Point[][] = [];
  private current: Point[] | null = null;
  private last: Point = { x: 0, y: 0 };

  moveTo(x: number, y: number): this {
    this.current = [{ x, y }];
    this.subpaths.push(this.current);
    this.last = { x, y };
    return this;
  }

  lineTo(x: number, y: number): this {
    if (!this.current) return this.moveTo(x, y);
    this.current.push({ x, y });
    this.last = { x, y };
    return this;
  }

  quadTo(cx: number, cy: number, x: number, y: number): this {
    const from = this.last;
    const steps = 12;
    for (let i = 1; i <= steps; i += 1) {
      const t = i / steps;
      const u = 1 - t;
      this.lineTo(u * u * from.x + 2 * u * t * cx + t * t * x, u * u * from.y + 2 * u * t * cy + t * t * y);
    }
    return this;
  }

  curveTo(c1x: number, c1y: number, c2x: number, c2y: number, x: number, y: number): this {
    const from = this.last;
    const steps = 16;
    for (let i = 1; i <= steps; i += 1) {
      const t = i / steps;
      const u = 1 - t;
      this.lineTo(
        u * u * u * from.x + 3 * u * u * t * c1x + 3 * u * t * t * c2x + t * t * t * x,
        u * u * u * from.y + 3 * u * u * t * c1y + 3 * u * t * t * c2y + t * t * t * y,
      );
    }
    return this;
  }

  /** An axis-aligned ellipse arc sweep, as a closed subpath. */
  ellipse(cx: number, cy: number, rx: number, ry: number, from = 0, to = Math.PI * 2): this {
    const steps = Math.max(12, Math.ceil(((to - from) / (Math.PI * 2)) * 48));
    for (let i = 0; i <= steps; i += 1) {
      const a = from + ((to - from) * i) / steps;
      const x = cx + Math.cos(a) * rx;
      const y = cy + Math.sin(a) * ry;
      if (i === 0) this.moveTo(x, y);
      else this.lineTo(x, y);
    }
    return this;
  }

  points(): Point[][] {
    return this.subpaths;
  }
}

/* -------------------------------------------------------------------------- */
/* The canvas                                                                 */
/* -------------------------------------------------------------------------- */

export class PixelCanvas {
  /** Logical size, in art pixels. */
  readonly width: number;
  readonly height: number;
  /** Buffer pixels per art pixel, in each direction. */
  readonly scale: number;
  /** Buffer size. */
  readonly bufWidth: number;
  readonly bufHeight: number;
  /** ABGR pixels, `bufWidth` x `bufHeight`. */
  readonly data: Uint32Array;
  /** Translation applied to every drawing call. */
  ox = 0;
  oy = 0;

  constructor(width: number, height: number, scale = ART_SCALE) {
    this.width = Math.ceil(width);
    this.height = Math.ceil(height);
    this.scale = scale;
    this.bufWidth = this.width * scale;
    this.bufHeight = this.height * scale;
    this.data = new Uint32Array(this.bufWidth * this.bufHeight);
  }

  /** Colour at an art-pixel position (fractions address the buffer more finely). */
  get(x: number, y: number): number {
    const bx = Math.floor(x * this.scale);
    const by = Math.floor(y * this.scale);
    if (bx < 0 || by < 0 || bx >= this.bufWidth || by >= this.bufHeight) return 0;
    return this.data[by * this.bufWidth + bx]!;
  }

  alphaAt(x: number, y: number): number {
    return this.get(x, y) >>> 24;
  }

  /* ---------------------------------------------------------------------- */
  /* Compositing                                                            */
  /* ---------------------------------------------------------------------- */

  /** Source-over blend of a colour at `alpha` (0-255, fractional) onto one buffer pixel. */
  private blend(i: number, r: number, g: number, b: number, alpha: number): void {
    if (alpha < 0.5) return;
    const data = this.data;
    if (alpha >= 254.5) {
      data[i] = ((255 << 24) | (clampByte(b) << 16) | (clampByte(g) << 8) | clampByte(r)) >>> 0;
      return;
    }
    const d = data[i]!;
    const da = d >>> 24;
    if (da === 0) {
      data[i] = ((Math.round(alpha) << 24) | (clampByte(b) << 16) | (clampByte(g) << 8) | clampByte(r)) >>> 0;
      return;
    }
    const sa = alpha / 255;
    const dk = (da / 255) * (1 - sa);
    const oa = sa + dk;
    const ks = sa / oa;
    const kd = dk / oa;
    data[i] =
      ((Math.round(oa * 255) << 24) |
        (clampByte(b * ks + ((d >>> 16) & 255) * kd) << 16) |
        (clampByte(g * ks + ((d >>> 8) & 255) * kd) << 8) |
        clampByte(r * ks + (d & 255) * kd)) >>>
      0;
  }

  private plot(x: number, y: number, coverage: number, p: Painter): void {
    if (coverage <= 0) return;
    if (!p.flat) p.sample(x + 0.5, y + 0.5);
    this.blend(y * this.bufWidth + x, p.r, p.g, p.b, coverage * p.a);
  }

  /* ---------------------------------------------------------------------- */
  /* Shapes                                                                 */
  /* ---------------------------------------------------------------------- */

  /** Buffer-space bounds for a rectangle given in art pixels (after translation). */
  private bounds(x0: number, y0: number, x1: number, y1: number): Bounds {
    const s = this.scale;
    return { x0: x0 * s, y0: y0 * s, x1: x1 * s, y1: y1 * s };
  }

  private painter(paint: Paint, b: Bounds): Painter {
    return new Painter(paint, b, this.scale, this.ox, this.oy);
  }

  /** One art pixel. */
  px(x: number, y: number, paint: Paint): void {
    const x0 = Math.floor(x + this.ox);
    const y0 = Math.floor(y + this.oy);
    this.fillBlock(x0, y0, x0 + 1, y0 + 1, paint);
  }

  /** A rectangle snapped to the art-pixel grid. */
  rect(x: number, y: number, w: number, h: number, paint: Paint): void {
    const x0 = Math.round(x + this.ox);
    const y0 = Math.round(y + this.oy);
    this.fillBlock(x0, y0, x0 + Math.round(w), y0 + Math.round(h), paint);
  }

  private fillBlock(x0: number, y0: number, x1: number, y1: number, paint: Paint): void {
    const s = this.scale;
    const p = this.painter(paint, this.bounds(x0, y0, x1, y1));
    const bx0 = Math.max(0, x0 * s);
    const by0 = Math.max(0, y0 * s);
    const bx1 = Math.min(this.bufWidth, x1 * s);
    const by1 = Math.min(this.bufHeight, y1 * s);
    for (let y = by0; y < by1; y += 1) {
      for (let x = bx0; x < bx1; x += 1) this.plot(x, y, 1, p);
    }
  }

  /** A rectangle at exact (fractional) art-pixel coordinates, with anti-aliased edges. */
  rectF(x: number, y: number, w: number, h: number, paint: Paint): void {
    const s = this.scale;
    const x0 = (x + this.ox) * s;
    const y0 = (y + this.oy) * s;
    const x1 = x0 + w * s;
    const y1 = y0 + h * s;
    const p = this.painter(paint, { x0, y0, x1, y1 });
    const ix0 = Math.max(0, Math.floor(x0));
    const iy0 = Math.max(0, Math.floor(y0));
    const ix1 = Math.min(this.bufWidth, Math.ceil(x1));
    const iy1 = Math.min(this.bufHeight, Math.ceil(y1));
    for (let y = iy0; y < iy1; y += 1) {
      const cy = Math.min(y + 1, y1) - Math.max(y, y0);
      for (let x = ix0; x < ix1; x += 1) {
        const cx = Math.min(x + 1, x1) - Math.max(x, x0);
        this.plot(x, y, cx * cy, p);
      }
    }
  }

  /** A rounded rectangle at exact coordinates. */
  rrect(x: number, y: number, w: number, h: number, radius: number, paint: Paint): void {
    const s = this.scale;
    const x0 = (x + this.ox) * s;
    const y0 = (y + this.oy) * s;
    const bw = w * s;
    const bh = h * s;
    const r = Math.min(radius * s, bw / 2, bh / 2);
    const cx = x0 + bw / 2;
    const cy = y0 + bh / 2;
    const hx = bw / 2 - r;
    const hy = bh / 2 - r;
    const p = this.painter(paint, { x0, y0, x1: x0 + bw, y1: y0 + bh });
    const ix0 = Math.max(0, Math.floor(x0 - 1));
    const iy0 = Math.max(0, Math.floor(y0 - 1));
    const ix1 = Math.min(this.bufWidth, Math.ceil(x0 + bw + 1));
    const iy1 = Math.min(this.bufHeight, Math.ceil(y0 + bh + 1));
    for (let yy = iy0; yy < iy1; yy += 1) {
      for (let xx = ix0; xx < ix1; xx += 1) {
        const qx = Math.abs(xx + 0.5 - cx) - hx;
        const qy = Math.abs(yy + 0.5 - cy) - hy;
        const sd = Math.hypot(Math.max(qx, 0), Math.max(qy, 0)) + Math.min(Math.max(qx, qy), 0) - r;
        this.plot(xx, yy, Math.min(1, Math.max(0, 0.5 - sd)), p);
      }
    }
  }

  /** Fills any convex polygon; either winding order works. `grow` fattens it (in buffer pixels) so abutting shapes leave no seam. */
  poly(points: readonly Point[], paint: Paint, grow = 0.5): void {
    if (points.length < 3) return;
    const s = this.scale;
    const pts = points.map((p) => ({ x: (p.x + this.ox) * s, y: (p.y + this.oy) * s }));

    let area = 0;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (let i = 0; i < pts.length; i += 1) {
      const a = pts[i]!;
      const b = pts[(i + 1) % pts.length]!;
      area += a.x * b.y - b.x * a.y;
      minX = Math.min(minX, a.x);
      minY = Math.min(minY, a.y);
      maxX = Math.max(maxX, a.x);
      maxY = Math.max(maxY, a.y);
    }
    const sign = area >= 0 ? 1 : -1;

    // Inward unit normals as a*x + b*y + c, positive inside.
    const edges: Array<[number, number, number]> = [];
    for (let i = 0; i < pts.length; i += 1) {
      const a = pts[i]!;
      const b = pts[(i + 1) % pts.length]!;
      const ex = b.x - a.x;
      const ey = b.y - a.y;
      const len = Math.hypot(ex, ey);
      if (len < 1e-9) continue;
      const nx = (-ey / len) * sign;
      const ny = (ex / len) * sign;
      edges.push([nx, ny, -(nx * a.x + ny * a.y)]);
    }
    if (edges.length < 3) return;

    const p = this.painter(paint, { x0: minX, y0: minY, x1: maxX, y1: maxY });
    const x0 = Math.max(0, Math.floor(minX - 1));
    const y0 = Math.max(0, Math.floor(minY - 1));
    const x1 = Math.min(this.bufWidth - 1, Math.ceil(maxX + 1));
    const y1 = Math.min(this.bufHeight - 1, Math.ceil(maxY + 1));
    const edgeCount = edges.length;
    const offset = 0.5 + grow;

    for (let y = y0; y <= y1; y += 1) {
      const cy = y + 0.5;
      for (let x = x0; x <= x1; x += 1) {
        const cx = x + 0.5;
        let d = Infinity;
        for (let i = 0; i < edgeCount; i += 1) {
          const e = edges[i]!;
          const v = e[0] * cx + e[1] * cy + e[2];
          if (v < d) {
            d = v;
            if (d < -offset) break;
          }
        }
        const cov = d + offset;
        if (cov > 0) this.plot(x, y, cov > 1 ? 1 : cov, p);
      }
    }
  }

  ellipse(cx: number, cy: number, rx: number, ry: number, paint: Paint): void {
    const s = this.scale;
    const bcx = (cx + this.ox) * s;
    const bcy = (cy + this.oy) * s;
    const brx = Math.max(rx * s, 1e-3);
    const bry = Math.max(ry * s, 1e-3);
    const p = this.painter(paint, { x0: bcx - brx, y0: bcy - bry, x1: bcx + brx, y1: bcy + bry });
    const x0 = Math.max(0, Math.floor(bcx - brx - 1));
    const y0 = Math.max(0, Math.floor(bcy - bry - 1));
    const x1 = Math.min(this.bufWidth - 1, Math.ceil(bcx + brx + 1));
    const y1 = Math.min(this.bufHeight - 1, Math.ceil(bcy + bry + 1));
    for (let y = y0; y <= y1; y += 1) {
      const dy = y + 0.5 - bcy;
      for (let x = x0; x <= x1; x += 1) {
        const dx = x + 0.5 - bcx;
        const k0 = Math.hypot(dx / brx, dy / bry);
        const k1 = Math.hypot(dx / (brx * brx), dy / (bry * bry));
        // First-order distance to the ellipse; negative inside.
        const sd = k1 < 1e-9 ? -Math.min(brx, bry) : (k0 * (k0 - 1)) / k1;
        const cov = 0.5 - sd;
        if (cov > 0) this.plot(x, y, cov > 1 ? 1 : cov, p);
      }
    }
  }

  /**
   * A straight line `width` art pixels thick with round ends. Endpoints are
   * pixel centres, so it lines up with what a one-pixel Bresenham line drew.
   */
  line(ax: number, ay: number, bx: number, by: number, paint: Paint, width = 1): void {
    const s = this.scale;
    const x0 = (ax + this.ox + 0.5) * s;
    const y0 = (ay + this.oy + 0.5) * s;
    const x1 = (bx + this.ox + 0.5) * s;
    const y1 = (by + this.oy + 0.5) * s;
    this.stroke(x0, y0, x1, y1, (width * s) / 2, paint);
  }

  /** A line between exact points (no half-pixel offset), for hand-drawn curves and details. */
  lineF(ax: number, ay: number, bx: number, by: number, paint: Paint, width = 1): void {
    const s = this.scale;
    this.stroke(
      (ax + this.ox) * s,
      (ay + this.oy) * s,
      (bx + this.ox) * s,
      (by + this.oy) * s,
      (width * s) / 2,
      paint,
    );
  }

  /** Strokes a connected run of points. */
  polyline(points: readonly Point[], paint: Paint, width = 1): void {
    for (let i = 0; i + 1 < points.length; i += 1) {
      this.lineF(points[i]!.x, points[i]!.y, points[i + 1]!.x, points[i + 1]!.y, paint, width);
    }
  }

  private stroke(x0: number, y0: number, x1: number, y1: number, half: number, paint: Paint): void {
    const p = this.painter(paint, {
      x0: Math.min(x0, x1) - half,
      y0: Math.min(y0, y1) - half,
      x1: Math.max(x0, x1) + half,
      y1: Math.max(y0, y1) + half,
    });
    const ix0 = Math.max(0, Math.floor(Math.min(x0, x1) - half - 1));
    const iy0 = Math.max(0, Math.floor(Math.min(y0, y1) - half - 1));
    const ix1 = Math.min(this.bufWidth - 1, Math.ceil(Math.max(x0, x1) + half + 1));
    const iy1 = Math.min(this.bufHeight - 1, Math.ceil(Math.max(y0, y1) + half + 1));
    const dx = x1 - x0;
    const dy = y1 - y0;
    const len2 = dx * dx + dy * dy;
    for (let y = iy0; y <= iy1; y += 1) {
      for (let x = ix0; x <= ix1; x += 1) {
        const px = x + 0.5 - x0;
        const py = y + 0.5 - y0;
        const t = len2 < 1e-9 ? 0 : Math.max(0, Math.min(1, (px * dx + py * dy) / len2));
        const dist = Math.hypot(px - dx * t, py - dy * t);
        const cov = half - dist + 0.5;
        if (cov > 0) this.plot(x, y, cov > 1 ? 1 : cov, p);
      }
    }
  }

  /**
   * Fills arbitrary closed outlines (concave, curved, with holes) using exact
   * area coverage. Build them with `PathBuilder`.
   */
  path(source: PathBuilder | readonly Point[][], paint: Paint): void {
    const subpaths = source instanceof PathBuilder ? source.points() : source;
    const s = this.scale;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    const loops = subpaths
      .filter((sp) => sp.length >= 3)
      .map((sp) =>
        sp.map((pt) => {
          const x = (pt.x + this.ox) * s;
          const y = (pt.y + this.oy) * s;
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
          return { x, y };
        }),
      );
    if (loops.length === 0) return;

    const gx = Math.max(0, Math.floor(minX) - 1);
    const gy = Math.max(0, Math.floor(minY) - 1);
    const gx1 = Math.min(this.bufWidth, Math.ceil(maxX) + 1);
    const gy1 = Math.min(this.bufHeight, Math.ceil(maxY) + 1);
    const w = gx1 - gx;
    const h = gy1 - gy;
    if (w <= 0 || h <= 0) return;
    const stride = w + 2;
    const acc = new Float32Array(stride * h + 2);

    const addLine = (ax: number, ay: number, bx: number, by: number): void => {
      if (ay === by) return;
      let dir = 1;
      let x = ax - gx;
      let y0 = ay - gy;
      let x1 = bx - gx;
      let y1 = by - gy;
      if (y0 > y1) {
        dir = -1;
        [x, x1] = [x1, x];
        [y0, y1] = [y1, y0];
      }
      const dxdy = (x1 - x) / (y1 - y0);
      let cx = x;
      if (y0 < 0) cx -= y0 * dxdy;
      const yStart = Math.max(0, Math.floor(y0));
      const yEnd = Math.min(h, Math.ceil(y1));
      for (let row = yStart; row < yEnd; row += 1) {
        const line = row * stride;
        const dy = Math.min(row + 1, y1) - Math.max(row, y0);
        const xNext = cx + dxdy * dy;
        const d = dy * dir;
        const lo = Math.min(cx, xNext);
        const hi = Math.max(cx, xNext);
        const loFloor = Math.floor(lo);
        const loI = loFloor;
        const hiCeil = Math.ceil(hi);
        const hiI = hiCeil;
        if (hiI <= loI + 1) {
          const xmf = 0.5 * (cx + xNext) - loFloor;
          acc[line + loI] = acc[line + loI]! + d - d * xmf;
          acc[line + loI + 1] = acc[line + loI + 1]! + d * xmf;
        } else {
          const inv = 1 / (hi - lo);
          const x0f = lo - loFloor;
          const a0 = 0.5 * inv * (1 - x0f) * (1 - x0f);
          const x1f = hi - hiCeil + 1;
          const am = 0.5 * inv * x1f * x1f;
          acc[line + loI] = acc[line + loI]! + d * a0;
          if (hiI === loI + 2) {
            acc[line + loI + 1] = acc[line + loI + 1]! + d * (1 - a0 - am);
          } else {
            const a1 = inv * (1.5 - x0f);
            acc[line + loI + 1] = acc[line + loI + 1]! + d * (a1 - a0);
            for (let xi = loI + 2; xi < hiI - 1; xi += 1) acc[line + xi] = acc[line + xi]! + d * inv;
            const a2 = a1 + (hiI - loI - 3) * inv;
            acc[line + hiI - 1] = acc[line + hiI - 1]! + d * (1 - a2 - am);
          }
          acc[line + hiI] = acc[line + hiI]! + d * am;
        }
        cx = xNext;
      }
    };

    for (const loop of loops) {
      for (let i = 0; i < loop.length; i += 1) {
        const a = loop[i]!;
        const b = loop[(i + 1) % loop.length]!;
        addLine(a.x, a.y, b.x, b.y);
      }
    }

    const p = this.painter(paint, { x0: minX, y0: minY, x1: maxX, y1: maxY });
    for (let row = 0; row < h; row += 1) {
      let sum = 0;
      for (let col = 0; col < w; col += 1) {
        sum += acc[row * stride + col]!;
        const cov = Math.min(1, Math.abs(sum));
        if (cov > 0.002) this.plot(gx + col, gy + row, cov, p);
      }
    }
  }

  /** Clears an art-pixel rectangle to transparent. */
  erase(x: number, y: number, w = 1, h = 1): void {
    const s = this.scale;
    const x0 = Math.max(0, Math.round(x + this.ox) * s);
    const y0 = Math.max(0, Math.round(y + this.oy) * s);
    const x1 = Math.min(this.bufWidth, (Math.round(x + this.ox) + w) * s);
    const y1 = Math.min(this.bufHeight, (Math.round(y + this.oy) + h) * s);
    for (let yy = y0; yy < y1; yy += 1) {
      for (let xx = x0; xx < x1; xx += 1) this.data[yy * this.bufWidth + xx] = 0;
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Whole-sprite passes                                                    */
  /* ---------------------------------------------------------------------- */

  /** Buffer pixels on the boundary of the opaque region: solid, with a see-through 4-neighbour. */
  private edgePixels(threshold = 128): number[] {
    const { bufWidth: w, bufHeight: h, data } = this;
    const solid = (x: number, y: number) =>
      x >= 0 && y >= 0 && x < w && y < h && data[y * w + x]! >>> 24 >= threshold;
    const edges: number[] = [];
    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        if (!solid(x, y)) continue;
        if (!solid(x - 1, y) || !solid(x + 1, y) || !solid(x, y - 1) || !solid(x, y + 1)) edges.push(y * w + x);
      }
    }
    return edges;
  }

  /**
   * Paints a border of `width` art pixels around every opaque region, beneath
   * the sprite, so the sprite grows outward and its interior is untouched.
   * `colorFor` may vary the border's colour by the pixel it hugs.
   */
  private border(width: number, colorFor: (source: number) => [number, number, number]): void {
    const { bufWidth: w, bufHeight: h, data } = this;
    const reach = width * this.scale;
    const radius = Math.ceil(reach) + 1;
    const best = new Float32Array(w * h).fill(Infinity);
    const from = new Int32Array(w * h);
    for (const source of this.edgePixels()) {
      const sx = source % w;
      const sy = (source - sx) / w;
      for (let dy = -radius; dy <= radius; dy += 1) {
        const y = sy + dy;
        if (y < 0 || y >= h) continue;
        for (let dx = -radius; dx <= radius; dx += 1) {
          const x = sx + dx;
          if (x < 0 || x >= w) continue;
          const d = Math.hypot(dx, dy);
          const i = y * w + x;
          if (d < best[i]!) {
            best[i] = d;
            from[i] = source;
          }
        }
      }
    }
    for (let i = 0; i < w * h; i += 1) {
      if (data[i]! >>> 24 === 255) continue;
      const d = best[i]!;
      const cov = Math.min(1, reach - d + 1);
      if (cov <= 0) continue;
      const [r, g, b] = colorFor(from[i]!);
      const existing = data[i]!;
      const ea = existing >>> 24;
      // The sprite's own (partly transparent) pixel goes over the border.
      const under = cov * 255;
      const outA = ea + under * (1 - ea / 255);
      if (outA < 0.5) continue;
      const wu = (under * (1 - ea / 255)) / outA;
      const we = 1 - wu;
      data[i] =
        ((Math.round(outA) << 24) |
          (clampByte(b * wu + ((existing >>> 16) & 255) * we) << 16) |
          (clampByte(g * wu + ((existing >>> 8) & 255) * we) << 8) |
          clampByte(r * wu + (existing & 255) * we)) >>>
        0;
    }
  }

  /**
   * Draw a border around every opaque region. Only transparent pixels are
   * painted, so the sprite grows outward and its interior is untouched.
   * (`diagonal` is accepted for old callers; the border is always round.)
   */
  outline(color: Color, _diagonal = false, width = OUTLINE_WIDTH): void {
    const packed = packColor(color);
    const rgb: [number, number, number] = [packed & 255, (packed >>> 8) & 255, (packed >>> 16) & 255];
    this.border(width, () => rgb);
  }

  /**
   * A softer outline for furniture: each border pixel takes a dark shade of
   * the colour it touches (so a red lantern gets a deep red edge, not black),
   * blended toward `base`, and lit top/left edges get a faint highlight first.
   */
  outlineSelective(base: Color, darken = 0.42, rim = 1.14, width = OUTLINE_WIDTH): void {
    const { bufWidth: w, bufHeight: h, data } = this;
    const baseRgb = [1, 3, 5].map((i) => parseInt(base.slice(i, i + 2), 16));

    // Rim light: a thin lighter line just inside top and left edges.
    const r = Math.max(1, Math.round(this.scale * 0.45));
    const solid = (x: number, y: number) =>
      x >= 0 && y >= 0 && x < w && y < h && data[y * w + x]! >>> 24 >= 128;
    const lit: number[] = [];
    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < w; x += 1) {
        if (solid(x, y) && (!solid(x - r, y) || !solid(x, y - r)) && solid(x + r, y) && solid(x, y + r)) {
          lit.push(y * w + x);
        }
      }
    }
    const rimmed = lit.map((i) => {
      const v = data[i]!;
      const f = rim - 1;
      const lighten = (c: number) => clampByte(c + (255 - c) * f);
      return ((v >>> 24) << 24 | lighten((v >>> 16) & 255) << 16 | lighten((v >>> 8) & 255) << 8 | lighten(v & 255)) >>> 0;
    });
    lit.forEach((i, k) => {
      data[i] = rimmed[k]!;
    });

    this.border(width, (source) => {
      const v = data[source]!;
      const rgb = [v & 255, (v >>> 8) & 255, (v >>> 16) & 255];
      const c = rgb.map((ch, i) => clampByte(ch * darken * 0.6 + baseRgb[i]! * 0.4));
      return [c[0]!, c[1]!, c[2]!];
    });
  }

  /** Mirror horizontally in place. */
  flipX(): this {
    const { bufWidth: width, bufHeight: height, data } = this;
    for (let y = 0; y < height; y += 1) {
      const row = y * width;
      for (let x = 0; x < width >> 1; x += 1) {
        const a = row + x;
        const b = row + width - 1 - x;
        const tmp = data[a]!;
        data[a] = data[b]!;
        data[b] = tmp;
      }
    }
    return this;
  }

  /** Composite another canvas of the same density on top (source-over). */
  blit(src: PixelCanvas, dx: number, dy: number): void {
    if (src.scale !== this.scale) throw new Error('blit needs canvases of the same scale');
    const s = this.scale;
    const offX = Math.round(dx + this.ox) * s;
    const offY = Math.round(dy + this.oy) * s;
    for (let y = 0; y < src.bufHeight; y += 1) {
      const ty = y + offY;
      if (ty < 0 || ty >= this.bufHeight) continue;
      for (let x = 0; x < src.bufWidth; x += 1) {
        const value = src.data[y * src.bufWidth + x]!;
        const a = value >>> 24;
        if (a === 0) continue;
        const tx = x + offX;
        if (tx < 0 || tx >= this.bufWidth) continue;
        this.blend(ty * this.bufWidth + tx, value & 255, (value >>> 8) & 255, (value >>> 16) & 255, a);
      }
    }
  }

  /** Multiply alpha of every opaque pixel, e.g. for translucent shadows or ghosts. */
  withAlpha(alpha: number): this {
    for (let i = 0; i < this.data.length; i += 1) {
      const value = this.data[i]!;
      const a = value >>> 24;
      if (a !== 0) this.data[i] = ((value & 0x00ffffff) | (clampByte(a * alpha) << 24)) >>> 0;
    }
    return this;
  }

  /** Copy cropped to the bounding box of opaque pixels (on the art-pixel grid), plus a margin. */
  trimmed(margin = 1): PixelCanvas {
    const s = this.scale;
    let minX = this.bufWidth;
    let minY = this.bufHeight;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < this.bufHeight; y += 1) {
      for (let x = 0; x < this.bufWidth; x += 1) {
        if (this.data[y * this.bufWidth + x]! >>> 24 === 0) continue;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
    if (maxX < 0) return new PixelCanvas(1, 1, s);
    const ax = Math.floor(minX / s);
    const ay = Math.floor(minY / s);
    const bx = Math.floor(maxX / s);
    const by = Math.floor(maxY / s);
    const out = new PixelCanvas(bx - ax + 1 + margin * 2, by - ay + 1 + margin * 2, s);
    out.blit(this, margin - ax, margin - ay);
    return out;
  }

  /**
   * The full-density image (or a rectangle of it, in art pixels). Show it at
   * `width` x `height` CSS pixels (times zoom).
   */
  toCanvas(region?: { x: number; y: number; width: number; height: number }): HTMLCanvasElement {
    const s = this.scale;
    const rx = (region?.x ?? 0) * s;
    const ry = (region?.y ?? 0) * s;
    const rw = (region?.width ?? this.width) * s;
    const rh = (region?.height ?? this.height) * s;
    const canvas = document.createElement('canvas');
    canvas.width = rw;
    canvas.height = rh;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas unavailable');
    const image = ctx.createImageData(rw, rh);
    const out = new Uint32Array(image.data.buffer);
    if (rx === 0 && ry === 0 && rw === this.bufWidth && rh === this.bufHeight) out.set(this.data);
    else {
      for (let y = 0; y < rh; y += 1) {
        const from = (ry + y) * this.bufWidth + rx;
        out.set(this.data.subarray(from, from + rw), y * rw);
      }
    }
    ctx.putImageData(image, 0, 0);
    return canvas;
  }
}
